import SDAxios from '../../http/requests/shop-drawings/index.js';
import ProjectAxios from '../../http/requests/projects/index.js';
import Promise from 'lodash/_Promise';

export default {
    /**
     * Retrieves Shop Drawing information.
     *
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getShopDrawing: function ({
        commit,
        dispatch,
    }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getShopDrawing(payload.shopDrawingId, payload.shopDrawingRevisionId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_SHOP_DRAWING', response.data.data);

                dispatch('rfi/setRfiId', response.data.data.revision.shop_drawing.rfi, { root: true }).then(() => {
                    if (payload.queryParams.hasOwnProperty('conversation') && payload.queryParams.conversation > 0) {
                        dispatch('rfi/setActiveConversation', Number(payload.queryParams.conversation), { root: true });
                    }
                    resolve(response);
                });

                if (typeof window.InfoSidebar !== 'undefined') {
                    if (response.data.data.revision.proposal) {
                        window.InfoSidebar.updateContext('proposal', response.data.data.revision.proposal.id,
                            'shop_drawings');
                    } else {
                        window.InfoSidebar.updateContext('project',
                            response.data.data.revision.shop_drawing.project.id, 'shop_drawings');
                    }
                }
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Retrieves Shop Drawing project information.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getProjectInfo: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getProjectInfo(payload.shopDrawingId, payload.shopDrawingRevisionId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_SHOP_DRAWING_PROJECT_INFO', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getProjectNPDTickets: async function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            ProjectAxios.getProjectNPDTickets(payload.projectId)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    commit('SET_PROJECT_NPD_TICKETS', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Set Draft Approval state
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    setDraftApproval: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.setDraftApproval(payload.shopDrawingRevisionDraftId, payload.approval, payload.note,
                payload.rejectionId, payload.attachments)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_DRAFT', {
                        draftData: response.data.data.draft,
                        index: payload.index,
                    });

                    commit('SET_REVISION_STATUS', response.data.data.status);

                    commit('SET_REVISION_PLANNED_DUE_DATE', response.data.data.planned_due_date);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Set Draft submitted state
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    setDraftSubmitted: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.setDraftSubmitted(payload.shopDrawingRevisionDraftId, payload.submitted, payload.note,
                payload.quoteAdjustmentsNeeded || undefined).then(
                response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_DRAFT', {
                        draftData: response.data.data.draft,
                        index: payload.index,
                    });

                    commit('SET_REVISION_STATUS', response.data.data.status);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Remove draft
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    removeDraft: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.removeDraft(payload.shopDrawingRevisionDraftId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('REMOVE_DRAFT', payload.index);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Upload new draft
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    storeUploadedDraft: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.storeUploadedDraft(
                payload.shopDrawingRevisionId,
                payload.files,
                payload.attachments,
                payload.installationDetailsSets,
                payload.installationDetailsRevised,
                payload.newPositions,
                payload.revisedPositions,
                payload.metalProfilesDetailsAttachments,
                payload.note,
                payload.quoteAdjustmentsNeeded,
            ).then(
                response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('APPEND_DRAFT', response.data.data.draft);

                    commit('SET_REVISION_STATUS', response.data.data.status);

                    commit('SET_DRAFT', {
                        draftData: response.data.data.draft,
                        index: payload.index,
                    });
                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Retrieves available drafters.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getDrafters: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getDrafters().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_DRAFTERS', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     *
     * @param commit
     * @returns {Promise<unknown>}
     */
    getRejections: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getRejections().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_REJECTIONS_LIST', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Opens signed Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    openSignedShopPrompt: function ({ commit }) {
        commit('TOGGLE_SIGNED_SHOPS_PROMPT', true);
    },

    /**
     * Closes signed Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    closeSignedShopPrompt: function ({ commit }) {
        commit('TOGGLE_SIGNED_SHOPS_PROMPT', false);
    },

    /**
     * Opens cancel Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    openCancelShopPrompt: function ({ commit }) {
        commit('TOGGLE_CANCEL_SHOPS_PROMPT', true);
    },

    /**
     * Opens Hold Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    openHoldShopPrompt: function ({ commit }) {
        commit('TOGGLE_HOLD_SHOPS_PROMPT', true);
    },

    /**
     * Closes cancel Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    closeHoldShopPrompt: function ({ commit }) {
        commit('TOGGLE_HOLD_SHOPS_PROMPT', false);
    },

    /**
     * Closes cancel Shop Drawing prompt.
     *
     * @param commit Vuex commit function
     */
    closeCancelShopPrompt: function ({ commit }) {
        commit('TOGGLE_CANCEL_SHOPS_PROMPT', false);
    },

    /**
     * Starts task information edit.
     *
     * @param commit Vuex commit function
     */
    startTaskInfoEdit: function ({ commit }) {
        commit('TOGGLE_TASK_INFO_EDIT', true);
    },

    /**
     * Ends task information edit.
     *
     * @param commit Vuex commit function
     */
    endTaskInfoEdit: function ({ commit }) {
        commit('TOGGLE_TASK_INFO_EDIT', false);
        commit('SET_TASK_INFO_ERRORS', {});
    },

    /**
     * Sends signed Shop Drawing to API.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    saveSignedShop: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.saveSignedShop(payload.shopDrawingRevisionId, payload.note, payload.file).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);

                commit('SET_SHOP_DRAWING', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Cancels Shop Drawing request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    cancelShopDrawingRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.cancelShopDrawingRequest(payload.shopDrawingRevisionId, payload.note).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);

                commit('SET_SHOP_DRAWING', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Holds Shop Drawing request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    holdShopDrawingRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.holdShopDrawingRequest(payload.shopDrawingRevisionId, payload.reason, payload.due_date,
                payload.status)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('TOGGLE_TASK_INFO_EDIT', false);

                    commit('SET_SHOP_DRAWING', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Unholds Shop Drawing request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    unholdShopDrawingRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.unholdShopDrawingRequest(payload.shopDrawingRevisionId)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('TOGGLE_TASK_INFO_EDIT', false);

                    commit('SET_SHOP_DRAWING', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Change Shop Drawing hold due date request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    holdShopDrawingDueDateChangeRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.holdShopDrawingDueDateChangeRequest(payload.shopDrawingRevisionId, payload.due_date)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    commit('SET_SHOP_DRAWING', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Requests for shops.
     *
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    requestForShops: function ({
        commit,
        dispatch,
    }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.requestForShops(payload.shopDrawingRevisionId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);
                commit('SET_SHOP_DRAWING', response.data.data);
                dispatch('setFailedAction', null);

                resolve(response);
            }).catch(error => {
                if  (error.response.data.message === 'Shop should be linked to Inst Details') {
                    commit('SHOW_IN_LINK_SHOP_MODAL_ONLY_INST_DETAILS', true);
                    commit('TOGGLE_LINK_SHOPS_REQUEST_PROMPT', true);

                    dispatch('setFailedAction', {
                        action: 'requestForShops',
                        data: { shopDrawingRevisionId: payload.shopDrawingRevisionId },
                    });
                }
                if (error.response.status === 422) {
                    commit('SET_TASK_INFO_ERRORS', error.response.data.data);
                    commit('TOGGLE_TASK_INFO_EDIT', true);
                    if (!payload.skipFailed) {
                        dispatch('setFailedAction', {
                            action: 'requestForShops',
                            data: { shopDrawingRevisionId: payload.shopDrawingRevisionId },
                        });
                    }
                }

                reject(error);
            });
        });
    },

    /**
     * Requests for revision.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    requestForRevision: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.requestForRevision(payload.shopDrawingRevisionId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_SHOP_DRAWING', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Gets project quotes.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getQuotes: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getQuotes(payload.projectId, payload.proposalId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_QUOTES', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Gets architectural drawings.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getArchitecturalDrawings: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getArchitecturalDrawings(payload.projectId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_ARCHITECTURAL_DRAWINGS', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Updates task information.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    updateTaskInformation: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.updateTaskInformation(payload.shopDrawingRevisionId, payload.data).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);
                commit('SET_SHOP_DRAWING', response.data.data);

                resolve(response);
            }).catch(error => {
                if (error.response.status === 422) {
                    commit('SET_TASK_INFO_ERRORS', error.response.data.data);
                }

                reject(error);
            });
        });
    },

    /**
     * Gets shop drawings list with auth handling.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getShopDrawingsList: function ({ commit }, payload) {
        return SDAxios.getShopDrawingsList(payload.httpOptions);
    },

    /**
     * Gets shop drawings action required list with auth handling.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getActionRequiredList: function ({ commit }, payload) {
        return SDAxios.getActionRequiredList(payload.httpOptions);
    },


    /**
     * Gets shop drawings revision hold statuses.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getShopDrawingRevisionHoldStatuses: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getShopDrawingRevisionHoldStatuses().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_HOLD_STATUSES', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Gets shop drawings action required unassigned count.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getActionRequiredListUnassignedCount: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getActionRequiredListUnassignedCount().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                commit('SET_UNASSIGNED_COUNT', response.data.data);
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getInstDetailsRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            if ([2, 3].includes(payload.typeId)) {
                SDAxios.getInstDetailsRequest(payload.shopId)
                    .then((response) => {
                        let linkedInstDetails = response.data.data;
                        if (linkedInstDetails !== null) {
                            commit('SET_LINKED_INST_DETAILS', linkedInstDetails);
                            resolve(response);
                        }
                    }).catch(error => {
                    if (error.response.status === 404) {
                        return;
                    }
                    reject(error);
                });
            } else {
                commit('SET_LINKED_INST_DETAILS', null);
                resolve();
            }
        });
    },

    getActionRequiredListAssignedCount: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getActionRequiredListAssignedCount().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                commit('SET_ASSIGNED_COUNT', response.data.data);
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getActionRequiredListWorkloadCount: function ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getActionRequiredListWorkloadCount().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                commit('SET_WORKLOAD_COUNT', response.data.data);
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getSignedShopDrawings: function ({ commit }, payload) {
        commit('SET_SINGLE_SHOP_DRAWINGS_LOADER', true);
        return new Promise((resolve, reject) => {
            ProjectAxios.getSignedShopDrawings(payload.project_id).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                commit('SET_SIGNED_SHOP_DRAWINGS', response.data.data.reverse());
                resolve(response);
            }).catch(error => {
                reject(error);
            }).finally(() => {
                commit('SET_SINGLE_SHOP_DRAWINGS_LOADER', false);
            });
        });
    },

    /**
     * Assigns drafter.
     *
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    assignDrafter: function ({
        commit,
        dispatch,
    }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.assignsDrafter(payload.shopDrawingRevisionId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);
                commit('SET_SHOP_DRAWING', response.data.data);

                dispatch('setFailedAction', null);

                resolve(response);
            }).catch(error => {
                if (error.response.status === 422) {
                    commit('SET_TASK_INFO_ERRORS', error.response.data.data);
                    commit('TOGGLE_TASK_INFO_EDIT', true);

                    dispatch('setFailedAction', {
                        action: 'assignDrafter',
                        data: { shopDrawingRevisionId: payload.shopDrawingRevisionId },
                    });
                }

                reject(error);
            });
        });
    },

    /**
     * Submit request to Drafter
     *
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    submitToDrafter: function ({
        commit,
        dispatch,
    }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.submitToDrafter(payload.shopDrawingRevisionId).then(response => {

                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('TOGGLE_TASK_INFO_EDIT', false);
                commit('SET_SHOP_DRAWING', response.data.data);

                dispatch('setFailedAction', null);

                resolve(response);
            }).catch(error => {
                if  (error.response.data.message === 'Shop should be linked to Inst Details') {
                    commit('SHOW_IN_LINK_SHOP_MODAL_ONLY_INST_DETAILS', true);
                    commit('TOGGLE_LINK_SHOPS_REQUEST_PROMPT', true);

                    dispatch('setFailedAction', {
                        action: 'submitToDrafter',
                        data: { shopDrawingRevisionId: payload.shopDrawingRevisionId },
                    });
                }
                if (error.response.status === 422) {
                    commit('SET_TASK_INFO_ERRORS', error.response.data.data);
                    commit('TOGGLE_TASK_INFO_EDIT', true);

                    dispatch('setFailedAction', {
                        action: 'submitToDrafter',
                        data: { shopDrawingRevisionId: payload.shopDrawingRevisionId },
                    });
                }

                reject(error);
            });
        });
    },

    /**
     * Gets shop drawings list with auth handling.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getShopDrawingsListToExcel: function ({ commit }, payload) {
        return SDAxios.getShopDrawingsListToExcel(payload.options);
    },

    /**
     * Get Shop Drawings file upload URL.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getUploadUrl: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {

            SDAxios.getUploadUrl(payload.type, payload.shopDrawingRevisionId, payload.fileName).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Resets Shop Drawing page state to initial values.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     */
    resetShopDrawingState: function ({
        commit,
        dispatch,
    }) {
        commit('SET_SHOP_DRAWING', {
            revision: null,
            options: { leed_project: [] },
        });
        commit('SET_SHOP_DRAWING_PROJECT_INFO', null);

        dispatch('endTaskInfoEdit');
        dispatch('closeCancelShopPrompt');
        dispatch('closeSignedShopPrompt');

        if (typeof window.InfoSidebar !== 'undefined') {
            window.InfoSidebar.updateContext(null, null);
        }
    },

    /**
     * Submit Additional request
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    submitAdditionalRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.submitAdditionalRequest(payload.additionalRequestId, payload.note, payload.file,
                payload.attachments).then(
                response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_SHOP_DRAWING_ADDITIONAL_REQUESTS', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Assign drafter to Additional request
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    assignDrafterToAdditionalRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.assignDrafterToAdditionalRequest(payload.additionalRequestId, payload.drafter_id).then(
                response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_SHOP_DRAWING_ADDITIONAL_REQUESTS', response.data.data);

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Wrapper function which calls actions and handles loading and notifications.
     *
     * @param dispatch Vuex dispatch function
     * @param payload Payload data
     */
    triggerActionWithLoadingAndNotifications ({ dispatch }, payload) {
        payload.context.$vs.loading();

        dispatch(payload.action, payload.data)
            .then(response => {
                payload.context.$vs.loading.close();
                if (!payload.skipMessages) {
                    payload.context.$vs.notify({
                        color: 'success',
                        title: 'Success',
                        text: response.data.message,
                    });
                }
            })
            .catch(error => {
                payload.context.$vs.loading.close();
                if (!payload.skipMessages) {
                    payload.context.$vs.notify({
                        title: 'Error',
                        time:8000,
                        text: error.response.data.message || error.message,
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger',
                    });
                }
            });
    },

    /**
     * Sets Shop Drawing failed action information.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     */
    setFailedAction ({ commit }, payload) {
        commit('SET_FAILED_ACTION', payload);
    },

    /**
     * Opens cancel Shop Drawing Additional Request prompt.
     *
     * @param commit Vuex commit function
     */
    openCancelShopAdditionalRequestPrompt: function ({ commit }) {
        commit('TOGGLE_CANCEL_SHOPS_ADDITIONAL_REQUEST_PROMPT', true);
    },

    /**
     * Closes cancel Shop Drawing Additional Request prompt.
     *
     * @param commit Vuex commit function
     */
    closeCancelShopAdditionalRequestPrompt: function ({ commit }) {
        commit('TOGGLE_CANCEL_SHOPS_ADDITIONAL_REQUEST_PROMPT', false);
    },

    /**
     * Opens New Shop Drawing Request prompt.
     *
     * @param commit Vuex commit function
     */
    openNewShopRequestPrompt: function ({ commit }) {
        commit('TOGGLE_NEW_SHOPS_REQUEST_PROMPT', true);
    },

    /**
     * Closes New Shop Drawing Request prompt.
     *
     * @param commit Vuex commit function
     */
    closeNewShopRequestPrompt: function ({ commit }) {
        commit('TOGGLE_NEW_SHOPS_REQUEST_PROMPT', false);
    },

    /**
     * Opens Link Shop Drawing Request prompt.
     *
     * @param commit Vuex commit function
     */
    openLinkShopRequestPrompt: function ({ commit }) {
        commit('TOGGLE_LINK_SHOPS_REQUEST_PROMPT', true);
    },

    /**
     * Closes Link Shop Drawing Request prompt.
     *
     * @param commit Vuex commit function
     */
    closeLinkShopRequestPrompt: function ({ commit }) {
        commit('TOGGLE_LINK_SHOPS_REQUEST_PROMPT', false);
    },

    /**
     * Cancels Shop Drawing Additional Request request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    cancelShopDrawingAdditionalRequest: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.cancelShopDrawingAdditionalRequest(payload.requestId, payload.note).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_SHOP_DRAWING_ADDITIONAL_REQUESTS', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    setShopDrawingDifficultyLevel: function ({ commit }, payload) {
        commit('SET_SHOP_DRAWING_DIFFICULTY_LEVEL', payload.difficulty_level);
    },

    /**
     * creates new Shop Drawing  Request request.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    createShopDrawing: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.createShopDrawing(payload.projectId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * creates link between Shop Drawings.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    linkShopDrawing: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.linkShopDrawingRequest(payload.a, payload.b).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    createNewInstDetailsForShop: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.createNewInstDetailsForShop(payload.a).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Retrieves Shops filters information.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getFiltersInformation ({ commit }) {
        return new Promise((resolve, reject) => {
            SDAxios.getFiltersInformation()
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_FILTERS_INFO', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets shop drawing list filters data.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    setShopDrawingListFilters ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('SET_FILTERS_DATA', data);

        dispatch('storeShopDrawingListFilters', state.shopsList.filters);
    },

    /**
     * Opens/closes shop drawing list filters sidebar.
     *
     * @param commit Vuex commit function
     * @param status Should sidebar be opened
     */
    toggleShopDrawingListFiltersSidebar ({ commit }, status) {
        commit('TOGGLE_FILTER_SIDEBAR', status);
    },

    /**
     * Removes single shop drawing list filter.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    removeShopDrawingListFilter ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('REMOVE_FILTER', data);

        dispatch('storeShopDrawingListFilters', state.shopsList.filters);
    },

    /**
     * Stores shop drawing list filters in local storage.
     *
     * @param payload Payload data
     */
    storeShopDrawingListFilters ({}, payload) {
        localStorage.setItem('shopDrawingListFilters', JSON.stringify(payload));
    },

    /**
     * Restores shop drawing list filters to the state.
     *
     * @param commit Vuex commit function
     */
    restoreShopDrawingListFilters ({ commit }) {
        let filters = localStorage.getItem('shopDrawingListFilters');

        if (filters === null) {
            return;
        }

        commit('SET_FILTERS_DATA', JSON.parse(filters));
    },

    /**
     * Triggers shops list refresh.
     *
     * @param commit Vuex commit function
     * @param status Should list be refreshed
     */
    toggleShopDrawingsListRefresh ({ commit }, status) {
        commit('TOGGLE_LIST_REFRESH', status);
    },

    /**
     * Sets shop drawing action required filters data.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    setShopDrawingActionRequiredListFilters ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('SET_ACTION_REQUIRED_FILTERS_DATA', data);

        dispatch('storeShopDrawingActionRequiredListFilters', state.actionRequiredList.filters);
    },

    /**
     * Opens/closes shop drawing ActionRequired list filters sidebar.
     *
     * @param commit Vuex commit function
     * @param status Should sidebar be opened
     */
    toggleShopDrawingActionRequiredListFiltersSidebar ({ commit }, status) {
        commit('TOGGLE_ACTION_REQUIRED_FILTER_SIDEBAR', status);
    },

    /**
     * Removes single shop drawing ActionRequired list filter.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    removeShopDrawingActionRequiredListFilter ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('REMOVE_ACTION_REQUIRED_FILTER', data);

        dispatch('storeShopDrawingActionRequiredListFilters', state.actionRequiredList.filters);
    },

    /**
     * Stores shop drawing ActionRequired filters in local storage.
     *
     * @param payload Payload data
     */
    storeShopDrawingActionRequiredListFilters ({}, payload) {
        localStorage.setItem('shopDrawingActionRequiredListFilters', JSON.stringify(payload));
    },

    /**
     * Restores shop drawing ActionRequired filters to the state.
     *
     * @param commit Vuex commit function
     */
    restoreShopDrawingActionRequiredListFilters ({ commit }) {
        let filters = localStorage.getItem('shopDrawingActionRequiredListFilters');

        if (filters === null) {
            return;
        }

        commit('SET_ACTION_REQUIRED_FILTERS_DATA', JSON.parse(filters));
    },

    /**
     * Triggers shops ActionRequired refresh.
     *
     * @param commit Vuex commit function
     * @param status Should list be refreshed
     */
    toggleShopDrawingsActionRequiredListRefresh ({ commit }, status) {
        commit('TOGGLE_ACTION_REQUIRED_LIST_REFRESH', status);
    },

    /**
     * Opens DWG Generation preloader modal.
     *
     * @param commit Vuex commit function
     */
    openDwgGenerationModal: function ({ commit }) {
        commit('TOGGLE_DWG_GENERATION_MODAL', true);
    },

    /**
     * Closes DWG Generation preloader modal.
     *
     * @param commit Vuex commit function
     */
    closeDwgGenerationModal: function ({ commit }) {
        commit('TOGGLE_DWG_GENERATION_MODAL', false);
    },

    /**
     * Opens DWG Generation preloader modal.
     *
     * @param commit Vuex commit function
     */
    open3dCadGenerationModal: function ({ commit }) {
        commit('TOGGLE_3D_CAD_GENERATION_MODAL', true);
    },
    open3dCadRegenerationModal: function ({ commit }) {
        commit('TOGGLE_3D_CAD_REGENERATION_MODAL', true);
    },

    /**
     * Closes DWG Generation preloader modal.
     *
     * @param commit Vuex commit function
     */
    close3dCadGenerationModal: function ({ commit }) {
        commit('TOGGLE_3D_CAD_GENERATION_MODAL', false);
    },
    close3dCadRegenerationModal: function ({ commit }) {
        commit('TOGGLE_3D_CAD_REGENERATION_MODAL', false);
    },

    getCadFile: function ({ _ }, payload) {
        return SDAxios.getCadFile(payload.revisionId);
    },
    clearPlannedDueDate ({ commit }) {
        commit('SET_REVISION_PLANNED_DUE_DATE', null);
    },

    createQuoteComparisonJob: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.createQuotComparisonJob(payload.shopId)
                .then((response) => {
                    let jobDetails = response.data.data;
                    if (jobDetails !== null) {
                        resolve(response);
                    }
                }).catch(error => {
                if (error.response.status === 404) {
                    return;
                }
                reject(error);
            });
        });
    },

    quoteComparisonJobDetails: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.quoteComparisonJobDetails(payload.jobId)
                .then((response) => {
                    let jobDetails = response.data.data;
                    if (jobDetails !== null) {
                        resolve(response);
                    }
                }).catch(error => {
                if (error.response.status === 404) {
                    return;
                }
                reject(error);
            });
        });
    },

    /**
     * Opens Shop Drawings est dates calculation modal.
     *
     * @param commit Vuex commit function
     */
    openEstDatesCalculationModal: function ({ commit }) {
        commit('TOGGLE_EST_DATE_CALCULATION_MODAL', true);
    },

    /**
     * Opens Shop Drawings est dates calculation modal.
     *
     * @param commit Vuex commit function
     */
    closeEstDatesCalculationModal: function ({ commit }) {
        commit('TOGGLE_EST_DATE_CALCULATION_MODAL', false);
    },

    startEstDatesCalculation () {
        return SDAxios.calculateEstDates();
    },

    /**
     * Gets revision request details.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getRevisionRequestDetails: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getRevisionRequestDetails(payload.proposalId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                commit('SET_BLUESCREEN_REVISION_DETAILS', response.data.data);
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Gets revision request details.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getCad3DDocumentLink: function ({ commit }, payload) {
      return new Promise((resolve, reject) => {
          SDAxios.getCad3DDocumentLink(payload.revisionId, payload.generateNew)
              .then(response => {
                  if (!response.data.success) {
                      reject({ message: response.data.message });
                  }

                  resolve(response)
              })
              .catch(error => {
                  reject(error);
              })
      })
    },

    getLinkedOrders: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.getLinkedOrders(payload.projectId, payload.proposalId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_LINKED_ORDERS', response.data.data);
                resolve(response);
            }).catch(error => {
                commit('SET_LINKED_ORDERS', []);
                reject(error);
            });
        });
    },

    addDraftNote: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.addDraftNote(payload.draftId, payload.note, payload.attachments).then(
                response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('ADD_DRAFT_COMMENT', {
                        comment: response.data.data,
                        index: payload.index,
                    });

                    resolve(response);
                }).catch(error => {
                reject(error);
            });
        });
    },
    calculateDifficultyLevel: function ({commit}, payload) {
        return new Promise((resolve, reject) => {
            SDAxios.calculateDifficultyLevel(payload.projectId, payload.proposalId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    }
};
